import type { ComponentType, SVGProps } from "react";
import {
  IdentityIcon,
  HistoryRouteIcon,
  OccupancyIcon,
  RegisterIcon,
  PhoneIcon,
  MobileIcon,
  MailIcon,
  CompanyConnIcon,
  RecordStatusIcon,
  VerificationIcon,
  LocationRefIcon,
} from "@/app/features/person-intelligence-icons";
import { MapPinIcon } from "@/components/feature-icons";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

export type IntelPoint = {
  id: string;
  num: number;
  title: string;
  icon: Icon;
  /** Constellation position, % of canvas (centre card is ~50/52). */
  pos: { x: number; y: number };
  short: string;
  description: string;
  canInclude: string[];
  benefit: string;
  /** Illustrative UI result state — clearly fictional. */
  result: string;
  /** Which "go deeper" pathway this point opens into. */
  pathway: PathwayId;
};

export type PathwayId =
  | "people"
  | "address"
  | "connections"
  | "company"
  | "property"
  | "contact";

/**
 * The 12 intelligence AREAS KleipData can check, assemble and present where
 * supported information is available. Not 12 guaranteed results.
 */
export const INTEL_POINTS: IntelPoint[] = [
  {
    id: "identity-match",
    num: 1,
    title: "Identity Match",
    icon: IdentityIcon,
    pos: { x: 50, y: 12 },
    short: "Establish the strongest available research path.",
    description:
      "Compare the supplied person details with supported available information and review potential matching records.",
    canInclude: [
      "Name",
      "Date of birth where available",
      "Address information",
      "Flexible matching",
      "Potential match information",
    ],
    benefit:
      "Start with incomplete information and establish the strongest available research path.",
    result: "Potential match",
    pathway: "people",
  },
  {
    id: "current-address",
    num: 2,
    title: "Current Address",
    icon: MapPinIcon,
    pos: { x: 71, y: 17 },
    short: "Move from the person into address-led research.",
    description:
      "Review supported more-recent address information associated with the person search.",
    canInclude: ["Occupants", "UPRN", "Property", "Location", "Connections"],
    benefit: "Move from the person into current address-led research.",
    result: "1 record",
    pathway: "address",
  },
  {
    id: "address-history",
    num: 3,
    title: "Address History",
    icon: HistoryRouteIcon,
    pos: { x: 86, y: 33 },
    short: "See how available address information has changed.",
    description:
      "Explore supported previous address-linked information over time.",
    canInclude: [
      "Historic addresses",
      "Address timeline",
      "Previous occupancy",
      "Shared address associations",
    ],
    benefit: "Understand how available address information has changed.",
    result: "4 records",
    pathway: "address",
  },
  {
    id: "occupancy",
    num: 4,
    title: "Occupancy & Address Associations",
    icon: OccupancyIcon,
    pos: { x: 90, y: 52 },
    short: "Understand the wider context around an address.",
    description:
      "Review available current and historical people associated with supported addresses.",
    canInclude: [
      "Current occupants",
      "Historic occupants",
      "Shared address records",
      "Address-linked associations",
    ],
    benefit:
      "Understand the wider context around an address. Shared occupancy does not automatically mean a personal relationship.",
    result: "6 associations",
    pathway: "address",
  },
  {
    id: "electoral",
    num: 5,
    title: "Electoral Information",
    icon: RegisterIcon,
    pos: { x: 86, y: 71 },
    short: "Add another source of person and address context.",
    description:
      "Surface supported electoral-register information where available and permitted.",
    canInclude: [
      "Electoral-register presence",
      "Address-linked electoral records",
    ],
    benefit: "Add another source of person and address context.",
    result: "Available",
    pathway: "people",
  },
  {
    id: "telephone",
    num: 6,
    title: "Telephone Information",
    icon: PhoneIcon,
    pos: { x: 71, y: 86 },
    short: "Create additional routes into contact and tracing workflows.",
    description:
      "Review available supported telephone information connected with the research workflow.",
    canInclude: ["Telephone lookup", "Reverse lookup", "Telephone append"],
    benefit: "Create additional routes into contact and tracing workflows.",
    result: "2 records",
    pathway: "contact",
  },
  {
    id: "mobile-validation",
    num: 7,
    title: "Mobile Validation",
    icon: MobileIcon,
    pos: { x: 50, y: 91 },
    short: "Improve the quality of available contact information.",
    description:
      "Review supported mobile-number validation and network information.",
    canInclude: [
      "Validation status",
      "Current network",
      "Original network",
      "Available status indicators",
    ],
    benefit: "Improve the quality of available contact information.",
    result: "Validated",
    pathway: "contact",
  },
  {
    id: "email-validation",
    num: 8,
    title: "Email Validation",
    icon: MailIcon,
    pos: { x: 29, y: 86 },
    short: "Add another contact and verification signal.",
    description:
      "Validate supported email information and use it as part of a wider research workflow where applicable.",
    canInclude: ["Email validation status", "Deliverability indicators"],
    benefit:
      "Add another contact and verification signal. Email validation does not prove identity by itself.",
    result: "Validated",
    pathway: "contact",
  },
  {
    id: "company-connections",
    num: 9,
    title: "Director & Company Connections",
    icon: CompanyConnIcon,
    pos: { x: 14, y: 71 },
    short: "Open a path into deeper corporate intelligence.",
    description:
      "Move from the person into supported corporate appointments and company information.",
    canInclude: [
      "Current directorships",
      "Former directorships",
      "Company details",
      "Co-directors",
      "Corporate appointments",
    ],
    benefit: "Open a path from person research into deeper corporate intelligence.",
    result: "2 connections",
    pathway: "company",
  },
  {
    id: "record-status",
    num: 10,
    title: "Record Status",
    icon: RecordStatusIcon,
    pos: { x: 10, y: 52 },
    short: "Help identify records requiring review.",
    description:
      "Surface supported bereavement or deceased-record indicators where available.",
    canInclude: ["Record-status indicators", "Review flags where returned"],
    benefit: "Improve data quality and help identify records requiring review.",
    result: "No indicator returned",
    pathway: "people",
  },
  {
    id: "verification",
    num: 11,
    title: "Verification Indicators",
    icon: VerificationIcon,
    pos: { x: 14, y: 33 },
    short: "Support your own review process.",
    description:
      "Use supported verification information to compare supplied person details with available records.",
    canInclude: [
      "Person verification",
      "Age-related verification where available",
      "Other supported verification indicators",
    ],
    benefit:
      "Support the customer's own review process. Not an identity guarantee or a compliance determination.",
    result: "Information available",
    pathway: "people",
  },
  {
    id: "location-uprn",
    num: 12,
    title: "Location & Property Reference",
    icon: LocationRefIcon,
    pos: { x: 29, y: 17 },
    short: "Move directly into geographic or property-led research.",
    description:
      "Connect supported address information with geographic and property-reference information.",
    canInclude: [
      "Coordinates",
      "Address geocoding",
      "UPRN where available",
      "Location context",
    ],
    benefit:
      "Move directly from person / address information into geographic or property-led research.",
    result: "Matched",
    pathway: "property",
  },
];

/* --------------------------------------------------------- go deeper */

export type Pathway = {
  id: PathwayId;
  label: string;
  flow: string[];
  cta: string;
  target: string;
};

export const PATHWAYS: Pathway[] = [
  {
    id: "people",
    label: "People",
    flow: [
      "Identity",
      "Matching",
      "Current information",
      "Historical information",
      "Verification",
      "Connections",
    ],
    cta: "Explore People Intelligence",
    target: "people-intelligence",
  },
  {
    id: "address",
    label: "Address",
    flow: [
      "Current address",
      "Occupancy",
      "Historic occupants",
      "UPRN",
      "Property",
      "Connections",
    ],
    cta: "Explore Address Intelligence",
    target: "address-intelligence",
  },
  {
    id: "connections",
    label: "Connections",
    flow: ["Person", "Address", "Occupants", "Company", "Property"],
    cta: "Explore Connections",
    target: "connections",
  },
  {
    id: "company",
    label: "Company",
    flow: [
      "Director appointment",
      "Company",
      "Directors",
      "PSC / ownership",
      "Filings",
      "Charges",
      "Company credit",
    ],
    cta: "Explore Company Intelligence",
    target: "company-intelligence",
  },
  {
    id: "property",
    label: "Property",
    flow: [
      "Address",
      "UPRN",
      "Property",
      "Title",
      "Registered ownership",
      "Person / company connection",
    ],
    cta: "Explore Property Intelligence",
    target: "property-intelligence",
  },
  {
    id: "contact",
    label: "Contact",
    flow: [
      "Telephone",
      "Reverse search",
      "Mobile validation",
      "Network",
      "Email",
      "Contact enrichment",
    ],
    cta: "Explore Contact Intelligence",
    target: "contact-record-status",
  },
];

/* ------------------------------------------------------ whole picture */

export const WHOLE_PICTURE = {
  know: ["Name", "Old address", "Telephone", "Email"],
  explore: ["Person", "Address", "Company", "Property", "Contact", "Connections"],
  use: ["Review", "Export", "CRM", "Casework", "API", "Bulk workflow"],
};

export const RESULT_STATE_NOTE =
  "Illustrative example values. A particular search may show information available, multiple records, a potential match, no supported result, or a service that is not enabled — the 12 points are intelligence areas KleipData can check, not guaranteed results.";
